import {
  type ComponentType,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type SVGProps,
} from "react";
import { useIonRouter } from "@ionic/react";
import { observer } from "mobx-react";
import {
  Activity,
  ArrowLeft,
  ChevronRight,
  Dumbbell,
  HeartPulse,
  Minus,
  PersonStanding,
  Plus,
  Ruler,
  Scale,
  ShieldCheck,
} from "lucide-react";
import {
  activityLevelSchema,
  contraindicationsSchema,
  desiredResultSchema,
  experienceSchema,
  healthInformationSchema,
  heightSchema,
  physiqueSchema,
  useUserService,
  weekTrainingAmountSchema,
  weightSchema,
} from "@entities/User";
import type { UserFullInfo } from "@entities/User/types/user.fullInfo";
import {
  createNumberOptions,
  InfoBottomSheet,
  OptionListPicker,
  TextSheetInput,
  WeightScalePicker,
  WheelPicker,
} from "@shared/ui/ProfileInfoForm";
import { Content, DefaultPage } from "@shared/layout/DefaultPage";
import { ROUTES } from "@shared/constants/routes";
import { toast } from "sonner";
import { BodyGlyph, HeartGlyph, TrophyGlyph } from "./profile-glyphs";
import physiqueFemaleEctomorphIcon from "@assets/images/physique/bodytype-female-ectomorph.png";
import physiqueFemaleEndomorphIcon from "@assets/images/physique/bodytype-female-endomorph.png";
import physiqueFemaleMesomorphIcon from "@assets/images/physique/bodytype-female-mesomorph.png";
import physiqueMaleEctomorphIcon from "@assets/images/physique/bodytype-male-ectomorph.png";
import physiqueMaleEndomorphIcon from "@assets/images/physique/bodytype-male-endomorph.png";
import physiqueMaleMesomorphIcon from "@assets/images/physique/bodytype-male-mesomorph.png";
import "./profile-details-page.css";

type ProfileDetailsTab = "body" | "health" | "targets";

type ProfileDetailsField =
  | "weight"
  | "height"
  | "physique"
  | "experienceLevel"
  | "activityLevel"
  | "healthInformation"
  | "contraindications"
  | "trainingsPerWeek"
  | "kilogramsDifferenceTarget";

type ProfileDetailsForm = Record<ProfileDetailsField, string>;

type ProfileTabItem = {
  type: ProfileDetailsTab;
  label: string;
  shortLabel: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
};

const PROFILE_TABS: ProfileTabItem[] = [
  {
    type: "body",
    label: "Данные о теле",
    shortLabel: "Тело",
    Icon: BodyGlyph,
  },
  {
    type: "health",
    label: "Здоровье",
    shortLabel: "Здоровье",
    Icon: HeartGlyph,
  },
  {
    type: "targets",
    label: "Цели",
    shortLabel: "Цели",
    Icon: TrophyGlyph,
  },
];

const PROFILE_DETAILS_THEME: Record<
  ProfileDetailsTab,
  {
    accent: string;
    accentBg: string;
    accentRgb: string;
  }
> = {
  body: {
    accent: "#285EDB",
    accentBg: "#EFF5FF",
    accentRgb: "40 94 219",
  },
  health: {
    accent: "#D9364D",
    accentBg: "#FFF0F2",
    accentRgb: "217 54 77",
  },
  targets: {
    accent: "#607000",
    accentBg: "#F6FCE4",
    accentRgb: "96 112 0",
  },
};

const PHYSIQUE_OPTIONS = [
  {
    value: "Эктоморф",
    label: "Эктоморф",
    description: "Стройное телосложение; вес и мышцы обычно набираются сложнее.",
    icons: {
      male: physiqueMaleEctomorphIcon,
      female: physiqueFemaleEctomorphIcon,
    },
  },
  {
    value: "Мезоморф",
    label: "Мезоморф",
    description: "Среднее или атлетичное телосложение; мышцы набираются проще.",
    icons: {
      male: physiqueMaleMesomorphIcon,
      female: physiqueFemaleMesomorphIcon,
    },
  },
  {
    value: "Эндоморф",
    label: "Эндоморф",
    description: "Более широкое телосложение; вес обычно набирается легче.",
    icons: {
      male: physiqueMaleEndomorphIcon,
      female: physiqueFemaleEndomorphIcon,
    },
  },
];
const EXPERIENCE_OPTIONS = ["Новичок", "Средний уровень", "Продвинутый"];
const ACTIVITY_OPTIONS = [
  "Сидячий образ жизни",
  "Среднеактивный образ жизни",
  "Высокоактивный образ жизни",
];

const FIELD_TITLES: Record<ProfileDetailsField, string> = {
  weight: "Вес",
  height: "Рост",
  physique: "Телосложение",
  experienceLevel: "Опыт тренировок",
  activityLevel: "Уровень активности",
  healthInformation: "Информация о здоровье",
  contraindications: "Противопоказания",
  trainingsPerWeek: "Тренировок в неделю",
  kilogramsDifferenceTarget: "Желаемый результат",
};

const WEIGHT_MIN = 35;
const WEIGHT_MAX = 250;
const WEIGHT_STEP = 0.1;
const HEIGHT_MIN = 100;
const HEIGHT_MAX = 250;
const WORKOUTS_MIN = 1;
const WORKOUTS_MAX = 99;
const RESULT_MIN = -200;
const RESULT_MAX = 200;
const GOAL_SAVE_DEBOUNCE_MS = 1500;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const normalizeNumber = (value: unknown): string => {
  if (value === undefined || value === null || value === "") {
    return "";
  }

  return String(value);
};

const buildFormData = (
  userData: Partial<UserFullInfo>,
): ProfileDetailsForm => ({
  weight: normalizeNumber(userData.weight),
  height: normalizeNumber(userData.height),
  physique: userData.physique || "",
  experienceLevel: userData.experienceLevel || "",
  activityLevel: userData.activityLevel || "",
  healthInformation: userData.healthInformation || "",
  contraindications: userData.contraindications || "",
  trainingsPerWeek: normalizeNumber(userData.trainingsPerWeek),
  kilogramsDifferenceTarget: normalizeNumber(
    userData.kilogramsDifferenceTarget,
  ),
});

const withUnit = (value: string, unit: string) =>
  value ? `${value} ${unit}` : "";

const validateField = (field: ProfileDetailsField, value: string): boolean => {
  const result =
    field === "weight"
      ? weightSchema.safeParse(value)
      : field === "height"
        ? heightSchema.safeParse(value)
        : field === "physique"
          ? physiqueSchema.safeParse(value)
          : field === "experienceLevel"
            ? experienceSchema.safeParse(value)
            : field === "activityLevel"
              ? activityLevelSchema.safeParse(value)
              : field === "healthInformation"
                ? healthInformationSchema.safeParse(value)
                : field === "contraindications"
                  ? contraindicationsSchema.safeParse(value)
                  : field === "trainingsPerWeek"
                    ? weekTrainingAmountSchema.safeParse(value)
                    : desiredResultSchema.safeParse(value);

  if (result.success) {
    return true;
  }

  toast.error(result.error.errors[0]?.message || "Некорректное значение");
  return false;
};

const toUpdatePayload = (
  field: ProfileDetailsField,
  value: string,
): Partial<UserFullInfo> => {
  if (field === "weight") {
    return { weight: Number(value) };
  }

  if (field === "height") {
    return { height: Number(value) };
  }

  if (field === "trainingsPerWeek") {
    return { trainingsPerWeek: Number(value) };
  }

  if (field === "kilogramsDifferenceTarget") {
    return { kilogramsDifferenceTarget: Number(value) };
  }

  return { [field]: value } as Partial<UserFullInfo>;
};

const ProfileDetailsFieldCard = ({
  icon,
  label,
  value,
  placeholder = "Не указано",
  onClick,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  placeholder?: string;
  onClick: () => void;
}) => (
  <button className="profile-details-card" type="button" onClick={onClick}>
    <span className="profile-details-card-icon">{icon}</span>
    <span className="profile-details-card-copy">
      <span>{label}</span>
      <strong className={!value ? "is-placeholder" : ""}>
        {value || placeholder}
      </strong>
    </span>
    <ChevronRight className="profile-details-card-chevron" size={22} />
  </button>
);

const GoalStepperCard = ({
  label,
  value,
  displayValue,
  min,
  max,
  onStep,
}: {
  label: string;
  value: string;
  displayValue: string;
  min: number;
  max: number;
  onStep: (direction: -1 | 1) => void;
}) => {
  const numericValue = Number(value);
  const hasNumericValue = value !== "" && !Number.isNaN(numericValue);
  const canDecrease = !hasNumericValue || numericValue > min;
  const canIncrease = !hasNumericValue || numericValue < max;

  return (
    <div className="profile-details-goal-card">
      <span className="profile-details-goal-copy">{label}</span>
      <span className="profile-details-stepper" role="group" aria-label={label}>
        <button
          type="button"
          aria-label={`Уменьшить: ${label}`}
          disabled={!canDecrease}
          onClick={(event) => {
            event.stopPropagation();
            onStep(-1);
          }}
        >
          <Minus size={20} strokeWidth={2.35} aria-hidden="true" />
        </button>
        <span
          className={`profile-details-stepper-value ${!value ? "is-placeholder" : ""}`.trim()}
          aria-live="polite"
        >
          {displayValue || "Не указано"}
        </span>
        <button
          type="button"
          aria-label={`Увеличить: ${label}`}
          disabled={!canIncrease}
          onClick={(event) => {
            event.stopPropagation();
            onStep(1);
          }}
        >
          <Plus size={20} strokeWidth={2.35} aria-hidden="true" />
        </button>
      </span>
    </div>
  );
};

export const ProfileDetailsPage = observer(
  ({
    activeTab,
    onTabChange,
  }: {
    activeTab: ProfileDetailsTab;
    onTabChange: (type: ProfileDetailsTab) => void;
  }) => {
    const nav = useIonRouter();
    const userService = useUserService();
    const [formData, setFormData] = useState<ProfileDetailsForm>(() =>
      buildFormData(userService?.data || {}),
    );
    const [activeField, setActiveField] = useState<ProfileDetailsField | null>(
      null,
    );
    const [draftValue, setDraftValue] = useState("");
    const goalSaveTimerRef = useRef<number | null>(null);
    const pendingGoalSaveRef = useRef<Partial<UserFullInfo> | null>(null);

    useEffect(() => {
      setFormData(buildFormData(userService?.data || {}));
    }, [userService?.data]);

    useEffect(() => {
      return () => {
        if (goalSaveTimerRef.current) {
          window.clearTimeout(goalSaveTimerRef.current);
          goalSaveTimerRef.current = null;
        }

        if (pendingGoalSaveRef.current) {
          const payload = pendingGoalSaveRef.current;
          pendingGoalSaveRef.current = null;
          if (userService) {
            void userService.sendNewDataInKc(
              { ...(userService.data || {}), ...payload },
              { silent: true },
            );
          }
        }
      };
    }, [userService]);

    const heightOptions = useMemo(
      () => createNumberOptions(HEIGHT_MIN, HEIGHT_MAX),
      [],
    );

    if (!userService) {
      return null;
    }

    const theme = PROFILE_DETAILS_THEME[activeTab];
    const themeStyle = {
      "--profile-details-accent": theme.accent,
      "--profile-details-accent-bg": theme.accentBg,
      "--profile-details-accent-rgb": theme.accentRgb,
    } as CSSProperties;

    const openEditor = (field: ProfileDetailsField) => {
      setActiveField(field);

      if (field === "weight") {
        setDraftValue(formData.weight || "75.0");
        return;
      }

      if (field === "height") {
        setDraftValue(formData.height || "165");
        return;
      }

      if (field === "trainingsPerWeek") {
        setDraftValue(formData.trainingsPerWeek || "5");
        return;
      }

      if (field === "kilogramsDifferenceTarget") {
        setDraftValue(formData.kilogramsDifferenceTarget || "-5");
        return;
      }

      setDraftValue(formData[field]);
    };

    const closeEditor = () => {
      setActiveField(null);
      setDraftValue("");
    };

    const saveField = async (field: ProfileDetailsField, value: string) => {
      const nextValue = value.trim();
      const currentValue = formData[field].trim();

      if (nextValue === currentValue) {
        return true;
      }

      if (!validateField(field, nextValue)) {
        return false;
      }

      setFormData((rest) => ({ ...rest, [field]: nextValue }));
      const payload = toUpdatePayload(field, nextValue);
      return userService.updateData(payload);
    };

    const commitEditor = async () => {
      if (!activeField) {
        return;
      }

      const success = await saveField(activeField, draftValue);
      if (success) {
        closeEditor();
      }
    };

    const stepGoal = (
      field: "trainingsPerWeek" | "kilogramsDifferenceTarget",
      direction: -1 | 1,
    ) => {
      const min = field === "trainingsPerWeek" ? WORKOUTS_MIN : RESULT_MIN;
      const max = field === "trainingsPerWeek" ? WORKOUTS_MAX : RESULT_MAX;
      const fallback = field === "trainingsPerWeek" ? 5 : -5;
      const current = Number(formData[field] || fallback);
      const nextValue = String(clamp(current + direction, min, max));
      const payload = toUpdatePayload(field, nextValue);

      setFormData((rest) => ({ ...rest, [field]: nextValue }));
      userService.setData(payload);
      pendingGoalSaveRef.current = {
        ...(pendingGoalSaveRef.current || {}),
        ...payload,
      };

      if (goalSaveTimerRef.current) {
        window.clearTimeout(goalSaveTimerRef.current);
      }

      goalSaveTimerRef.current = window.setTimeout(() => {
        const pendingPayload = pendingGoalSaveRef.current;
        pendingGoalSaveRef.current = null;
        void userService.sendNewDataInKc(
          { ...(userService.data || {}), ...(pendingPayload || {}) },
          { silent: true },
        );
        goalSaveTimerRef.current = null;
      }, GOAL_SAVE_DEBOUNCE_MS);
    };

    const goBackToProfile = () => {
      nav.push(ROUTES.overall.profile, "back", "replace");
    };

    const renderSectionSwitch = () => (
      <div
        className="profile-details-tabs"
        role="group"
        aria-label="Раздел профиля"
      >
        <span className="profile-details-tabs-highlight" aria-hidden="true" />
        {PROFILE_TABS.map((tab) => {
          const Icon = tab.Icon;

          return (
            <button
              key={tab.type}
              className={tab.type === activeTab ? "is-active" : undefined}
              type="button"
              aria-label={tab.label}
              aria-pressed={tab.type === activeTab}
              onClick={() => onTabChange(tab.type)}
            >
              <Icon className="profile-details-tabs-icon" />
              <span
                className="profile-details-tabs-label"
                aria-hidden={tab.type !== activeTab}
              >
                {tab.shortLabel}
              </span>
            </button>
          );
        })}
      </div>
    );

    const renderBodyTab = () => (
      <div className="profile-details-list">
        <ProfileDetailsFieldCard
          icon={<Scale size={23} />}
          label="Вес"
          value={withUnit(formData.weight, "кг")}
          onClick={() => openEditor("weight")}
        />
        <ProfileDetailsFieldCard
          icon={<Ruler size={23} />}
          label="Рост"
          value={withUnit(formData.height, "см")}
          onClick={() => openEditor("height")}
        />
        <ProfileDetailsFieldCard
          icon={<PersonStanding size={25} />}
          label="Телосложение"
          value={formData.physique}
          onClick={() => openEditor("physique")}
        />
        <ProfileDetailsFieldCard
          icon={<Dumbbell size={23} />}
          label="Опыт тренировок"
          value={formData.experienceLevel}
          onClick={() => openEditor("experienceLevel")}
        />
      </div>
    );

    const renderHealthTab = () => (
      <div className="profile-details-list">
        <ProfileDetailsFieldCard
          icon={<Activity size={23} />}
          label="Уровень активности"
          value={formData.activityLevel}
          onClick={() => openEditor("activityLevel")}
        />
        <ProfileDetailsFieldCard
          icon={<HeartPulse size={23} />}
          label="Информация о здоровье"
          value={formData.healthInformation}
          placeholder="Нет проблем со здоровьем"
          onClick={() => openEditor("healthInformation")}
        />
        <ProfileDetailsFieldCard
          icon={<ShieldCheck size={23} />}
          label="Противопоказания"
          value={formData.contraindications}
          placeholder="Нет"
          onClick={() => openEditor("contraindications")}
        />
      </div>
    );

    const renderTargetsTab = () => (
      <div className="profile-details-list profile-details-list--goals">
        <GoalStepperCard
          label="Максимально допустимое количество тренировок в неделю"
          value={formData.trainingsPerWeek}
          displayValue={formData.trainingsPerWeek}
          min={WORKOUTS_MIN}
          max={WORKOUTS_MAX}
          onStep={(direction) => stepGoal("trainingsPerWeek", direction)}
        />
        <GoalStepperCard
          label="Желаемый результат"
          value={formData.kilogramsDifferenceTarget}
          displayValue={withUnit(formData.kilogramsDifferenceTarget, "кг")}
          min={RESULT_MIN}
          max={RESULT_MAX}
          onStep={(direction) =>
            stepGoal("kilogramsDifferenceTarget", direction)
          }
        />
      </div>
    );

    const renderEditorContent = () => {
      if (activeField === "weight") {
        const value = clamp(Number(draftValue || 75), WEIGHT_MIN, WEIGHT_MAX);

        return (
          <WeightScalePicker
            value={value}
            min={WEIGHT_MIN}
            max={WEIGHT_MAX}
            step={WEIGHT_STEP}
            onChange={(nextValue) => setDraftValue(nextValue.toFixed(1))}
          />
        );
      }

      if (activeField === "height") {
        const value = clamp(Number(draftValue || 165), HEIGHT_MIN, HEIGHT_MAX);

        return (
          <div className="auth-info-height-picker">
            <WheelPicker
              ariaLabel="Выбор роста"
              className="auth-info-number-wheel"
              options={heightOptions}
              value={value}
              onChange={(nextValue) => setDraftValue(String(nextValue))}
            />
            <span className="auth-info-height-unit">см</span>
          </div>
        );
      }

      if (activeField === "physique") {
        const iconVariant = userService.data.sex === "Женский" ? "female" : "male";
        const physiqueOptions = PHYSIQUE_OPTIONS.map((option) => ({
          value: option.value,
          label: option.label,
          description: option.description,
          icon: option.icons[iconVariant],
        }));

        return (
          <div className="profile-details-physique-sheet">
            <p className="profile-details-physique-note">
              Ориентируйтесь на то, как тело обычно набирает вес и мышечную
              массу. Это нужно только для настройки тренировок и питания.
            </p>
            <OptionListPicker
              options={physiqueOptions}
              value={draftValue}
              onChange={setDraftValue}
            />
          </div>
        );
      }

      if (activeField === "experienceLevel") {
        return (
          <OptionListPicker
            options={EXPERIENCE_OPTIONS}
            value={draftValue}
            onChange={setDraftValue}
          />
        );
      }

      if (activeField === "activityLevel") {
        return (
          <OptionListPicker
            options={ACTIVITY_OPTIONS}
            value={draftValue}
            onChange={setDraftValue}
          />
        );
      }

      if (
        activeField === "healthInformation" ||
        activeField === "contraindications"
      ) {
        return (
          <TextSheetInput
            placeholder={
              activeField === "healthInformation"
                ? "Например, нет проблем со здоровьем"
                : "Например, нет"
            }
            value={draftValue}
            onChange={setDraftValue}
          />
        );
      }

      if (activeField === "trainingsPerWeek") {
        return (
          <div className="profile-details-number-sheet">
            <WheelPicker
              ariaLabel="Выбор количества тренировок"
              className="auth-info-number-wheel"
              options={createNumberOptions(WORKOUTS_MIN, WORKOUTS_MAX)}
              value={clamp(Number(draftValue || 5), WORKOUTS_MIN, WORKOUTS_MAX)}
              onChange={(nextValue) => setDraftValue(String(nextValue))}
            />
          </div>
        );
      }

      if (activeField === "kilogramsDifferenceTarget") {
        return (
          <TextSheetInput
            placeholder="Например, -5"
            value={draftValue}
            onChange={setDraftValue}
          />
        );
      }

      return null;
    };

    return (
      <DefaultPage noPadding className="auth-info-page profile-details-page">
        <Content className="profile-details-content">
          <div className="profile-details-shell" style={themeStyle}>
            <header className="profile-details-header">
              <h1>Профиль</h1>
            </header>

            <main className="profile-details-main">
              <h2>
                {activeTab === "body" && "Данные о теле"}
                {activeTab === "health" && "Данные о здоровье"}
                {activeTab === "targets" && "Цели"}
              </h2>
              {activeTab === "body" && renderBodyTab()}
              {activeTab === "health" && renderHealthTab()}
              {activeTab === "targets" && renderTargetsTab()}
            </main>
          </div>
          <div className="profile-details-tabs-dock" slot="fixed">
            <div className="profile-details-dock-inner">
              <button
                className="profile-details-back"
                type="button"
                aria-label="Назад"
                onClick={goBackToProfile}
              >
                <ArrowLeft size={25} strokeWidth={2.35} aria-hidden="true" />
              </button>
              {renderSectionSwitch()}
            </div>
          </div>
        </Content>

        {activeField && (
          <InfoBottomSheet
            title={FIELD_TITLES[activeField]}
            modifier={activeField}
            onClose={closeEditor}
            onCommit={() => void commitEditor()}
          >
            {renderEditorContent()}
          </InfoBottomSheet>
        )}
      </DefaultPage>
    );
  },
);
